import StarterKit from '@tiptap/starter-kit';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { TableKit } from '@tiptap/extension-table';
import { generateHTML } from '@tiptap/html';
import DOMPurify from 'dompurify';
import type { JSONContent } from '@tiptap/core';

// One shared extension list so the editor, the read-only renderer, and the
// version history all agree on the document schema.
export const guideExtensions = [
  StarterKit.configure({
    link: { openOnClick: false },
  }),
  TaskList,
  TaskItem.configure({ nested: true }),
  TableKit.configure({ table: { resizable: false } }),
];

export const EMPTY_GUIDE_DOC: JSONContent = { type: 'doc', content: [] };

// Typography for guide content, applied to both the editor and read-only
// views. The app has no typography plugin, so the element styles live here.
export const GUIDE_TYPOGRAPHY_CLASS = [
  'text-sm leading-6 text-gray-800',
  '[&_h1]:mt-4 [&_h1]:mb-1 [&_h1]:text-xl [&_h1]:font-semibold',
  '[&_h2]:mt-4 [&_h2]:mb-1 [&_h2]:text-lg [&_h2]:font-semibold',
  '[&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:text-base [&_h3]:font-semibold',
  '[&_p]:my-1.5',
  '[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-6',
  '[&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-6',
  '[&_li]:my-0.5',
  '[&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-1',
  '[&_ul[data-type=taskList]_li]:flex [&_ul[data-type=taskList]_li]:gap-2',
  '[&_ul[data-type=taskList]_input]:mt-1.5 [&_ul[data-type=taskList]_input]:accent-blue-600',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-200 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600',
  '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-gray-900 [&_pre]:p-3 [&_pre]:text-xs [&_pre]:text-gray-100',
  '[&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[13px]',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-gray-100',
  '[&_a]:text-blue-600 [&_a]:underline',
  '[&_table]:my-3 [&_table]:w-full [&_table]:border-collapse',
  '[&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-50 [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-medium',
  '[&_td]:border [&_td]:border-gray-300 [&_td]:px-2 [&_td]:py-1.5',
  '[&_hr]:my-4 [&_hr]:border-gray-200',
].join(' ');

// Render a stored guide document to sanitized HTML for read-only views
// (current page, version history, conflict comparison).
export const renderGuideHtml = (content: unknown): string => {
  if (!content || typeof content !== 'object') return '';
  try {
    return DOMPurify.sanitize(
      generateHTML(content as JSONContent, guideExtensions),
    );
  } catch {
    return '';
  }
};
